import { useState, type FormEvent, type ReactNode } from "react";
import { can } from "@sts/schemas";
import type {
  DocumentContent,
  DocumentIntegrity,
  DocumentSend,
  Job as JobRow,
  ListedDocument,
  Me,
  SendDocumentInput,
  SigningLinkResult,
  SignatureEnvelope,
  SignatureMode,
  SignatureVerification,
} from "../api/types.js";
import { Async, Failure } from "../components/Async.jsx";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { useSubmit } from "../app/use-submit.js";
import { href } from "../app/router.js";
import { isoDay, longDate } from "../lib/format.js";
import {
  DEFAULT_LINK_EXPIRY_DAYS,
  DOCUMENT_STATUS,
  ENVELOPE_STATUS,
  MAX_LINK_EXPIRY_DAYS,
  SEND_MODE_LABEL,
  SIGNATURE_MODES,
  SIGNATURE_MODE_LABEL,
  describeSendMode,
  currentVersions,
  documentStatusWord,
  documentTypeLabel,
  envelopeStatusWord,
  parseRecipients,
  supersededLine,
  supersessionOf,
  versionWord,
} from "../lib/documents.js";

/**
 * The evidence vault for one job.
 *
 * Three things this screen is careful about.
 *
 * It never shows a storage key. The API withholds it on purpose — it is an
 * internal path into a bucket, and publishing it invites a client to fetch the
 * bytes around the integrity check that `/content` exists to perform — so there
 * is nothing in the wire shape to render by accident, and nothing here reaches
 * for one.
 *
 * A superseded document reads as superseded. It is still issued and still
 * immutable, so a screen that shows only "Issued" is true and dangerous at
 * once: it invites somebody to email a customer the version that was replaced.
 *
 * And sending is treated as what it is — an irreversible act aimed at somebody
 * outside the company. The form does not send. It assembles what would be sent,
 * shows it in full, and waits.
 */

interface Vault {
  readonly job: JobRow;
  readonly documents: readonly ListedDocument[];
  readonly me: Me;
}

export function JobDocuments({ jobId }: { readonly jobId: string }): ReactNode {
  const { client } = usePortal();

  /** Raised after a send, so the log below re-reads rather than going stale. */
  const [sendNonce, setSendNonce] = useState(0);

  const { state, reload } = useAsync<Vault>(async () => {
    // Both reads are the same permission on the same job, so there is nothing
    // to salvage if one fails: `listJobDocuments` and `getJob` authorise
    // identically. Failing together is honest here. `me` is a third, unrelated
    // grant, but every role that can reach this screen at all holds it.
    const [job, documents, me] = await Promise.all([
      client.getJob(jobId),
      client.jobDocuments(jobId),
      client.me(),
    ]);
    return { job, documents, me };
  }, [client, jobId]);

  return (
    <Async state={state} what="the documents on this job" reload={reload}>
      {(vault) => (
        <>
          <div className="page-header">
            <p className="small">
              <a href={href.job(vault.job.id)}>Back to the job</a>{" "}
              <span className="muted">·</span>{" "}
              <a href={href.documentFinder()}>Which document do I need?</a>{" "}
              <span className="muted">·</span>{" "}
              <a href={href.jobContract(vault.job.id)}>What was agreed</a>
            </p>
            <h1>Documents</h1>
            <p className="lede">{vault.job.reference}</p>
          </div>

          <GeneratePanel jobId={vault.job.id} onGenerated={reload} />

          <section>
            <h2>
              In the vault <span className="count">{vault.documents.length}</span>
            </h2>
            {vault.documents.length === 0 ? (
              <p className="empty">
                Nothing on this job yet. Produce one below.
              </p>
            ) : (
              <>
                <p className="caption-note small muted">
                  Newest first. The date is when the document was produced: the
                  API records whether a document has been issued but not when,
                  so no issue date is claimed here.
                </p>
                <CurrentVersions documents={vault.documents} />
                {vault.documents.map((doc) => (
                  <DocumentCard
                    key={doc.id}
                    doc={doc}
                    all={vault.documents}
                    me={vault.me}
                    onSent={() => setSendNonce((n) => n + 1)}
                    onIssued={reload}
                  />
                ))}
              </>
            )}
          </section>

          <SendLog jobId={jobId} nonce={sendNonce} />
        </>
      )}
    </Async>
  );
}

/** The DOM id of a document's card, so "go to the current version" can scroll to it. */
function cardId(documentId: string): string {
  return `document-${documentId}`;
}

/**
 * Scrolls a document's card into view and moves focus to it. A button, not a
 * #fragment link: the portal's hash router owns the fragment.
 */
function showCard(documentId: string): void {
  const card = document.getElementById(cardId(documentId));
  card?.scrollIntoView?.({ block: "start" });
  card?.focus?.();
}

/**
 * Where to look first, when a job holds more than one version of something:
 * the current installer pack (and any other document that has been revised),
 * named with its version and date, above the list that also holds the old
 * ones. An installer opening the vault is pointed at the pack to build from
 * before they meet the one it replaced.
 */
function CurrentVersions({ documents }: { readonly documents: readonly ListedDocument[] }): ReactNode {
  const current = currentVersions(documents);
  if (current.length === 0) return null;
  return (
    <div className="notice" role="note" data-testid="current-versions">
      <ul className="small">
        {current.map((doc) => (
          <li key={doc.id}>
            <strong>
              Current {documentTypeLabel(doc.type).toLowerCase()}: {versionWord(doc) ?? doc.title}
            </strong>
            , produced {longDate(doc.createdAt)}. Earlier versions below are superseded.{" "}
            <button type="button" className="link" onClick={() => showCard(doc.id)}>
              Show it
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DocumentCard({
  doc,
  all,
  me,
  onSent,
  onIssued,
}: {
  readonly doc: ListedDocument;
  /** The job's whole list, to say which version replaced this one. */
  readonly all: readonly ListedDocument[];
  readonly me: Me;
  readonly onSent: () => void;
  readonly onIssued: () => void;
}): ReactNode {
  const status = DOCUMENT_STATUS[doc.status];
  const version = versionWord(doc);
  const replaced = supersededLine(doc, all, longDate);
  const current = supersessionOf(doc, all)?.current ?? null;

  return (
    <article
      id={cardId(doc.id)}
      tabIndex={-1}
      data-document-id={doc.id}
      className={doc.status === "superseded" ? "room has-assumptions" : "room"}
    >
      <h3>
        {doc.title}{" "}
        <span className={`status-word status-${statusTone(doc.status)}`}>
          {documentStatusWord(doc.status)}
        </span>
      </h3>

      <p className="small muted">
        {documentTypeLabel(doc.type)}
        {version ? ` · ${version}` : ""} · produced {longDate(doc.createdAt)}
      </p>

      {replaced !== null ? (
        <p className="notice warn small" role="note" data-testid="superseded-by">
          <strong>{replaced}</strong>{" "}
          {current ? (
            <button type="button" className="link" onClick={() => showCard(current.id)}>
              Go to the current version{versionWord(current) ? ` (${versionWord(current)})` : ""}
            </button>
          ) : null}
        </p>
      ) : null}

      <p className="small">{status?.meaning ?? ""}</p>

      <Issue doc={doc} me={me} onIssued={onIssued} />

      <dl className="fields">
        <div className="row">
          <dt>Template version</dt>
          <dd className="mono small">{doc.templateVersion}</dd>
        </div>
        <div className="row">
          <dt>Rules version</dt>
          <dd>
            {doc.rulesVersion === null ? (
              <span className="muted">Not recorded</span>
            ) : (
              <span className="mono small">{doc.rulesVersion}</span>
            )}
          </dd>
        </div>
        <div className="row">
          <dt>Engine version</dt>
          <dd>
            {doc.engineVersion === null ? (
              <span className="muted">Not recorded</span>
            ) : (
              <span className="mono small">{doc.engineVersion}</span>
            )}
          </dd>
        </div>
        <div className="row">
          <dt>Content hash</dt>
          <dd>
            {doc.contentHash === null ? (
              <span className="muted">Nothing rendered yet</span>
            ) : (
              <span className="mono small">{doc.contentHash}</span>
            )}
          </dd>
        </div>
        <div className="row">
          <dt>Keep until</dt>
          <dd>
            {doc.retainUntil === null ? (
              <span className="muted">No retention recorded</span>
            ) : (
              <>
                {longDate(doc.retainUntil)}{" "}
                <span className="muted small">
                  the obligation expires then; it is not a deletion date
                </span>
              </>
            )}
          </dd>
        </div>
      </dl>

      <Preview doc={doc} />
      <Integrity doc={doc} />
      <SendPanel doc={doc} onSent={onSent} />
      <CopyLinkPanel doc={doc} />
      <SignaturePanel doc={doc} />
    </article>
  );
}

/**
 * Fixes a draft as evidence: hashed, immutable, retained.
 *
 * The step the send and signature panels are both waiting on. Gated on the
 * same `document: issue` grant the API enforces, asked of the matrix rather
 * than guessed from the role name — a button that can only ever come back
 * refused is worse than its absence, so a role without the grant is told why
 * in one sentence rather than shown a control that cannot work.
 */
function Issue({
  doc,
  me,
  onIssued,
}: {
  readonly doc: ListedDocument;
  readonly me: Me;
  readonly onIssued: () => void;
}): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();

  // Nothing to do once it is issued or superseded — issuing only ever moves a
  // document one way, from draft to fixed.
  if (doc.status !== "draft") return null;

  if (!doc.hasContent) {
    return (
      <p className="small muted">
        Nothing has been rendered against this record yet, so there is
        nothing here to fix as evidence.
      </p>
    );
  }

  const mayIssue = can({ role: me.role, resource: "document", action: "issue" });

  if (!mayIssue) {
    return (
      <p className="small muted">
        Your role may not <span className="mono">issue</span>{" "}
        <span className="mono">document</span>, so this draft cannot be fixed
        as evidence from here — sending it and raising a signature request
        stay unavailable until somebody who holds that grant does.
      </p>
    );
  }

  return (
    <div className="notice pending">
      <p>
        <strong>Still a draft.</strong> Issuing fixes these exact bytes as
        evidence — the database refuses any change to them from that point on
        — and is what unlocks sending this document and raising a signature
        request against it.
      </p>
      <button
        disabled={busy}
        onClick={() =>
          void submit(async () => {
            await client.issueDocument(doc.id);
            onIssued();
          })
        }
      >
        {busy ? "Issuing…" : "Issue this document"}
      </button>
      {error !== null ? (
        <Failure error={error} what="issuing this document" />
      ) : null}
    </div>
  );
}

/**
 * The document's own bytes, fetched only when asked and rendered where they
 * cannot touch anything.
 *
 * Re-verified by the API on every request — a document whose stored bytes no
 * longer match the recorded hash is refused outright rather than served with
 * a warning — so reaching a rendered preview at all is itself part of what it
 * proves. Not fetched eagerly: a job can carry many documents, several of them
 * may hold a customer's personal data, and there is no reason to pull every
 * one of them over the wire before anybody has asked to read it.
 */
function Preview({ doc }: { readonly doc: ListedDocument }): ReactNode {
  const { client } = usePortal();
  const [state, setState] = useState<
    | { readonly status: "closed" }
    | { readonly status: "loading" }
    | { readonly status: "open"; readonly content: DocumentContent }
    | { readonly status: "failed"; readonly error: unknown }
  >({ status: "closed" });

  if (!doc.hasContent) {
    return (
      <p className="small muted">
        No file is stored against this record yet, so there is nothing to
        preview.
      </p>
    );
  }

  async function open(): Promise<void> {
    setState({ status: "loading" });
    try {
      const content = await client.documentContent(doc.id);
      setState({ status: "open", content });
    } catch (cause: unknown) {
      setState({ status: "failed", error: cause });
    }
  }

  return (
    <div className="preview">
      {state.status === "open" ? (
        <button
          className="secondary"
          onClick={() => setState({ status: "closed" })}
        >
          Hide the preview
        </button>
      ) : (
        <button
          className="secondary"
          disabled={state.status === "loading"}
          onClick={() => void open()}
        >
          {state.status === "loading" ? "Loading…" : "Preview this document"}
        </button>
      )}
      <p className="hint small">
        Fetches the API&rsquo;s own re-verified bytes and renders them
        sandboxed — no script, no form, no navigation, no reach into this
        page&rsquo;s storage.
      </p>
      {state.status === "failed" ? (
        <Failure error={state.error} what="a preview of this document" />
      ) : null}
      {state.status === "open" ? (
        <DocumentFrame html={state.content.html} title={`Preview of ${doc.title}`} />
      ) : null}
    </div>
  );
}

/**
 * A document, rendered where it cannot touch anything.
 *
 * An `<iframe srcDoc>` with an empty `sandbox` attribute rather than
 * `dangerouslySetInnerHTML`, and the difference is a security boundary rather
 * than a preference. This HTML is produced by the installer's own templates
 * and rendered inside a page that holds this browser's own bearer token, so
 * injected straight into the DOM a crafted document would be running on this
 * origin — it could read the token out of storage, or draw a convincing form
 * over the top of the page. An empty `sandbox` withholds every capability the
 * platform allows to be withheld — no scripts, no forms, no navigation, no
 * popups, and a unique opaque origin that cannot reach this one's storage —
 * so the worst a document can be is wrong, rather than dangerous. The API
 * sends its own Content-Security-Policy with the bytes too; both are here
 * because they fail differently, a header can be stripped by an intermediary
 * and an attribute can be dropped in a refactor.
 */
function DocumentFrame({
  html,
  title,
}: {
  readonly html: string;
  readonly title: string;
}): ReactNode {
  return (
    <iframe
      className="paper"
      title={title}
      srcDoc={html}
      sandbox=""
      referrerPolicy="no-referrer"
    />
  );
}

/** Reuses the deadline board's colour words so the palette stays one language. */
function statusTone(status: ListedDocument["status"]): string {
  if (status === "superseded") return "critical";
  if (status === "draft") return "approaching";
  return "tracking";
}

/**
 * The integrity check, asked rather than assumed.
 *
 * The answer is the API's own: a failed check is a 200 with a reason, because
 * "are these still the bytes you recorded?" is a question that deserves a true
 * answer rather than an error page.
 */
function Integrity({ doc }: { readonly doc: ListedDocument }): ReactNode {
  const { client } = usePortal();
  const [result, setResult] = useState<DocumentIntegrity | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  if (!doc.hasContent) {
    return (
      <p className="small muted">
        No file is stored against this record yet, so there is nothing to check.
      </p>
    );
  }

  async function check(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      setResult(await client.verifyDocument(doc.id));
    } catch (cause: unknown) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  return (
    <p className="pending">
      <button className="secondary" disabled={busy} onClick={() => void check()}>
        {busy ? "Checking…" : "Check the stored file"}
      </button>{" "}
      {result === null ? (
        <span className="muted small">
          Re-hashes the stored bytes and compares them with the hash recorded
          when the document was produced.
        </span>
      ) : result.ok ? (
        <span className="status-word status-tracking">
          Intact <span className="muted">· the stored bytes match the recorded hash</span>
        </span>
      ) : (
        <span className="status-word status-breached">
          Failed <span className="muted">· {result.reason}</span>
        </span>
      )}
      {error !== null ? <Failure error={error} what="that check" /> : null}
    </p>
  );
}

// --------------------------------------------------------------- producing

/**
 * The document types this screen offers to produce.
 *
 * A short list rather than all fourteen the engine can make: these are the
 * ones an office actually reaches for from a job, in the order the work
 * happens. The rest are produced from the screens that own them.
 */
const GENERATABLE: readonly { readonly type: string; readonly label: string }[] = [
  { type: "quotation", label: "Quotation" },
  { type: "contract", label: "Contract of sale" },
  { type: "right-to-cancel", label: "Notice of the right to cancel" },
  { type: "variation-to-contract", label: "Variation to contract" },
  { type: "handover-checklist", label: "Handover checklist" },
  { type: "completion-of-works", label: "Completion of works" },
  { type: "workmanship-warranty", label: "Workmanship warranty" },
];

/**
 * Produce a document for this job.
 *
 * WHY THIS WAS MISSING, because the gap is instructive. The engine, the
 * fourteen templates and `POST /api/jobs/:jobId/documents/:type/generate` have
 * all existed for some time; this screen said "the API has no route that
 * generates one" and offered nothing. So the only documents anybody could act
 * on were ones that had somehow appeared by other means — which, on a fresh
 * job, meant none, and no way to find out why.
 *
 * A REFUSAL IS THE USEFUL ANSWER HERE, not an error to tidy away. The
 * templates refuse rather than render a compliance document with a blank
 * where a legally required term belongs, and they name every missing item.
 * That list IS the instruction for what to go and fill in, so it is shown
 * exactly as the API worded it rather than replaced with "could not generate".
 */
function GeneratePanel({
  jobId,
  onGenerated,
}: {
  readonly jobId: string;
  readonly onGenerated: () => void;
}): ReactNode {
  const { client } = usePortal();
  const { submit, busy, error } = useSubmit();
  const [type, setType] = useState("contract");

  const onGenerate = async (): Promise<void> => {
    await submit(async () => {
      await client.generateDocument(jobId, type);
      onGenerated();
    });
  };

  return (
    <section>
      <h2>Produce a document</h2>
      {error !== null ? <Failure error={error} what="producing that document" /> : null}
      <p className="hint small">
        Produced as a draft, so you can read it before issuing it. Issuing is
        what fixes it as evidence and unlocks sending and signing.
      </p>
      <div className="row">
        <div className="field">
          <label htmlFor="generate-type">Document</label>
          <select
            id="generate-type"
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            {GENERATABLE.map((g) => (
              <option key={g.type} value={g.type}>
                {g.label}
              </option>
            ))}
          </select>
        </div>
        <p>
          <button type="button" disabled={busy} onClick={() => void onGenerate()}>
            {busy ? "Producing…" : "Generate"}
          </button>
        </p>
      </div>
    </section>
  );
}

// ------------------------------------------------------------ link to copy

/**
 * A signing link to hand over by some means other than our email.
 *
 * WHY THIS SITS BESIDE THE SEND PANEL RATHER THAN INSIDE IT. Emailing a
 * contract and copying a link are different acts with different evidence.
 * A send writes an `email_sends` row — recipient, document hash, timestamp —
 * which an MCS audit reads as proof the customer was sent their paperwork.
 * Minting a link writes no such row, because nobody was sent anything: the
 * installer is about to put it in a text message, or read it down the phone,
 * and this system cannot witness that. Folding the two together would either
 * lose the evidence or fabricate it.
 *
 * THE LINK IS SHOWN ONCE AND NOT KEPT. The server stores only its hash, so
 * what comes back here is the only copy that will ever exist. It is held in
 * component state and nowhere else — not localStorage, not the URL — and
 * "Done" throws it away, because a signing link left on a laptop screen in a
 * customer's kitchen is a credential anybody walking past can photograph.
 */
function CopyLinkPanel({ doc }: { readonly doc: ListedDocument }): ReactNode {
  const { client } = usePortal();
  const [link, setLink] = useState<SigningLinkResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<unknown>(null);

  if (!doc.issued) return null;

  const mint = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      setLink(await client.signingLink(doc.id));
      setCopied(false);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const copy = async (): Promise<void> => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
    } catch {
      // Clipboard access can be refused, and over plain http it always is.
      // The link is on screen and selectable either way, so say so rather
      // than leaving a button that silently does nothing.
      setError(
        new Error(
          "This browser would not let the page copy for you. Select the link above and copy it by hand.",
        ),
      );
    }
  };

  return (
    <div className="stacked">
      {error !== null ? <Failure error={error} what="a signing link" /> : null}

      {link === null ? (
        <>
          <p className="small muted">
            Or get a link to send yourself — by text message, or read out over
            the phone. It opens the same agreement, and nothing is recorded as
            sent because nobody was sent anything.
          </p>
          <button type="button" onClick={() => void mint()} disabled={busy}>
            {busy ? "Making a link…" : "Get a link to copy"}
          </button>
        </>
      ) : (
        <>
          <p className="small">
            <strong>Copy this now.</strong> It is shown once — we keep only a
            fingerprint of it, so if you lose it you will need a new one.
          </p>
          <p className="mono small">{link.url}</p>
          <div className="row">
            <button type="button" onClick={() => void copy()}>
              {copied ? "Copied" : "Copy link"}
            </button>
            <button
              type="button"
              className="secondary"
              onClick={() => {
                setLink(null);
                setCopied(false);
              }}
            >
              Done
            </button>
          </div>
          <p className="small muted">
            Stops working on {longDate(link.expiresAt)}. Anyone who has this
            link can open the agreement, so send it to the customer and nobody
            else.
          </p>
        </>
      )}
    </div>
  );
}

// --------------------------------------------------------------- sending

type SendStage =
  | { readonly name: "form" }
  | { readonly name: "confirming"; readonly input: SendDocumentInput }
  | {
      readonly name: "sent";
      readonly input: SendDocumentInput;
      /** False when nothing actually left the building — see the notice below. */
      readonly delivered: boolean;
      readonly note?: string;
    };

/**
 * Email a document to somebody outside the company.
 *
 * The two-step shape is the point. Everything a send commits to — which
 * document, which bytes, which addresses, which subject, and whether the file
 * itself leaves the building — is assembled, printed, and left sitting there
 * until a person presses the second button. Nothing reaches the API before
 * that, and the panel says so in as many words, because "did I just send that?"
 * is the question this screen exists to answer.
 */
function SendPanel({
  doc,
  onSent,
}: {
  readonly doc: ListedDocument;
  readonly onSent: () => void;
}): ReactNode {
  const { client } = usePortal();
  const [recipients, setRecipients] = useState("");
  const [subject, setSubject] = useState(doc.title);
  const [message, setMessage] = useState("");
  const [mode, setMode] = useState<SendDocumentInput["mode"]>("secure-link");
  const [expiry, setExpiry] = useState(String(DEFAULT_LINK_EXPIRY_DAYS));
  const [stage, setStage] = useState<SendStage>({ name: "form" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  if (!doc.issued) {
    return (
      <p className="small muted">
        A draft cannot be emailed. The API refuses it{" "}
        <span className="mono small">document_not_issued</span> so that what a
        recipient receives is what the evidence vault holds.
      </p>
    );
  }

  const addresses = parseRecipients(recipients);
  const days = Number(expiry);
  const expiryValid =
    mode === "attachment" ||
    (Number.isInteger(days) && days >= 1 && days <= MAX_LINK_EXPIRY_DAYS);
  // The API requires a subject and a body and refuses blanks with a 400.
  // Finding that out by pressing Send is a worse way to learn it, so the form
  // asks for what the route demands.
  const complete =
    addresses.length > 0 &&
    subject.trim() !== "" &&
    message.trim() !== "" &&
    expiryValid;

  function review(event: FormEvent): void {
    event.preventDefault();
    if (!complete) return;
    setError(null);
    setStage({
      name: "confirming",
      input: {
        recipients: addresses,
        subject,
        body: message,
        mode,
        // The API refuses an expiry on an attachment rather than ignoring it,
        // so one is only ever offered where it means something.
        ...(mode === "secure-link" ? { linkExpiryDays: days } : {}),
      },
    });
  }

  async function send(input: SendDocumentInput): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const result = await client.sendDocument(doc.id, input);
      // An older API does not answer the question at all; treat that as the
      // delivery it has always claimed rather than inventing a failure.
      const delivered = result.delivered !== false;
      setStage({
        name: "sent",
        input,
        delivered,
        ...(result.note ? { note: result.note } : {}),
      });
      setRecipients("");
      setMessage("");
      onSent();
    } catch (cause: unknown) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  return (
    <details className="send">
      <summary>Send this document by email</summary>

      {stage.name === "confirming" ? (
        <Confirmation
          doc={doc}
          input={stage.input}
          busy={busy}
          onSend={() => void send(stage.input)}
          onBack={() => setStage({ name: "form" })}
        />
      ) : (
        <form className="stacked" onSubmit={review}>
          {stage.name === "sent" ? (
            stage.delivered ? (
              <div className="notice" role="status">
                <p>
                  Sent to {stage.input.recipients.join(", ")}. It is in the send
                  log below, which is the record an assessor would ask for.
                </p>
              </div>
            ) : (
              // Never "sent" for a message that went nowhere: the send log is
              // compliance evidence, and this one says failed.
              <div className="notice notice--warn" role="alert">
                <p>
                  <strong>Nothing was sent.</strong>{" "}
                  {stage.note ??
                    "This deployment has no email delivery configured, so the message was not delivered."}{" "}
                  Nobody at {stage.input.recipients.join(", ")} received it, and
                  the send log records it as failed rather than as evidence.
                </p>
              </div>
            )
          ) : null}

          <div className="field">
            <label htmlFor={`to-${doc.id}`}>Send to</label>
            <input
              id={`to-${doc.id}`}
              value={recipients}
              placeholder="customer@example.com"
              onChange={(e) => setRecipients(e.target.value)}
            />
            <p className="hint small">
              Separate several addresses with commas. The API checks each one
              and names the one that is wrong.
            </p>
          </div>

          <div className="field">
            <label htmlFor={`subject-${doc.id}`}>Subject</label>
            <input
              id={`subject-${doc.id}`}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor={`message-${doc.id}`}>Message</label>
            <textarea
              id={`message-${doc.id}`}
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
            <p className="hint small">
              Required. The API refuses a send with no subject or no message,
              and an email that says nothing is not something to put in front of
              a customer.
            </p>
          </div>

          <div className="field">
            <label htmlFor={`mode-${doc.id}`}>How it goes</label>
            <select
              id={`mode-${doc.id}`}
              value={mode}
              onChange={(e) =>
                setMode(e.target.value as SendDocumentInput["mode"])
              }
            >
              {(["secure-link", "attachment"] as const).map((option) => (
                <option key={option} value={option}>
                  {SEND_MODE_LABEL[option]}
                </option>
              ))}
            </select>
            <p className="hint small">{describeSendMode(mode, days)}</p>
          </div>

          {mode === "secure-link" ? (
            <div className="field">
              <label htmlFor={`expiry-${doc.id}`}>Link valid for</label>
              <input
                id={`expiry-${doc.id}`}
                inputMode="numeric"
                value={expiry}
                aria-invalid={expiryValid ? undefined : "true"}
                onChange={(e) => setExpiry(e.target.value)}
              />
              <p className="hint small">
                Days. The API accepts 1 to {MAX_LINK_EXPIRY_DAYS} — a link good
                for longer than a year is a permanent credential in somebody
                else&rsquo;s mailbox.
              </p>
            </div>
          ) : null}

          <button type="submit" disabled={!complete}>
            Review what will be sent
          </button>
          <p className="hint small">
            This button does not send anything. It shows you the email first.
          </p>
        </form>
      )}

      {error !== null ? <Failure error={error} what="that send" /> : null}
    </details>
  );
}

function Confirmation({
  doc,
  input,
  busy,
  onSend,
  onBack,
}: {
  readonly doc: ListedDocument;
  readonly input: SendDocumentInput;
  readonly busy: boolean;
  readonly onSend: () => void;
  readonly onBack: () => void;
}): ReactNode {
  return (
    <div className="notice pending" role="alert">
      <p>
        <strong>Nothing has been sent yet.</strong> This is what will leave when
        you press Send.
      </p>

      {doc.status === "superseded" ? (
        <p className="notice fail">
          This document has been superseded by a newer version. The API will
          still send it — it is you, not the software, deciding that the
          replaced version is the one the recipient should have.
        </p>
      ) : null}

      <dl className="fields">
        <div className="row">
          <dt>Document</dt>
          <dd>
            {doc.title}{" "}
            <span className="muted small">
              {documentTypeLabel(doc.type)} ·{" "}
              {documentStatusWord(doc.status)}
            </span>
          </dd>
        </div>
        <div className="row">
          <dt>These exact bytes</dt>
          <dd className="mono small">
            {doc.contentHash ?? "no hash recorded"}
          </dd>
        </div>
        <div className="row">
          <dt>To</dt>
          <dd>
            <ul>
              {input.recipients.map((address) => (
                <li key={address}>{address}</li>
              ))}
            </ul>
          </dd>
        </div>
        <div className="row">
          <dt>Subject</dt>
          <dd>{input.subject}</dd>
        </div>
        <div className="row">
          <dt>Message</dt>
          <dd>{input.body}</dd>
        </div>
        <div className="row">
          <dt>Delivery</dt>
          <dd>
            {SEND_MODE_LABEL[input.mode]}
            <br />
            <span className="small">
              {describeSendMode(
                input.mode,
                input.linkExpiryDays ?? DEFAULT_LINK_EXPIRY_DAYS,
              )}
            </span>
          </dd>
        </div>
      </dl>

      <p>
        <button disabled={busy} onClick={onSend}>
          {busy ? "Sending…" : "Send now"}
        </button>{" "}
        <button className="secondary" disabled={busy} onClick={onBack}>
          Go back and change it
        </button>
      </p>
    </div>
  );
}

/** What has already left, which is the record MCS asks an installer to hold. */
function SendLog({
  jobId,
  nonce,
}: {
  readonly jobId: string;
  readonly nonce: number;
}): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync<DocumentSend[]>(
    () => client.jobSends(jobId),
    [client, jobId, nonce],
  );

  return (
    <section>
      <h2>Sent from this job</h2>
      <Async state={state} what="the send log" reload={reload}>
        {(sends) =>
          sends.length === 0 ? (
            <p className="empty">
              Nothing has been emailed from this job.
            </p>
          ) : (
            <div className="table-scroll">
              <table>
                <caption>
                  Every send is recorded. MCS requires the performance estimate
                  to reach the customer before contract award, and this is what
                  turns that from a claim into a record.
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Sent</th>
                    <th scope="col">To</th>
                    <th scope="col">Subject</th>
                    <th scope="col">How</th>
                    <th scope="col">Delivery</th>
                  </tr>
                </thead>
                <tbody>
                  {sends.map((send) => (
                    <tr key={send.id}>
                      <td className="ts">{isoDay(send.createdAt)}</td>
                      <td>{send.recipients.join(", ")}</td>
                      <td>{send.subject}</td>
                      <td>{SEND_MODE_LABEL[send.mode] ?? send.mode}</td>
                      {/* The provider's own word for it. The API records
                          "sent" at the point of handover and updates this from
                          delivery webhooks; inventing "delivered" here would be
                          claiming something nobody has told us. */}
                      <td>{send.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </Async>
    </section>
  );
}

// ------------------------------------------------------------ signatures

interface EnvelopeState {
  readonly envelope: SignatureEnvelope;
  /** Which call the status came from, so the screen can date its own claim. */
  readonly reportedFor: "raised" | "withdrawn";
}

/**
 * Signature status, limited to what the API will tell us.
 *
 * There is no route that lists the envelopes on a document, and none that reads
 * one back by id. `POST .../signature-envelopes`, `POST .../void` and
 * `GET .../verify` are the whole surface. So this panel can only report an
 * envelope this browser raised, and it reports the status the API returned at
 * the moment it returned it rather than implying it is current. An envelope
 * raised on somebody else's screen is invisible here, and saying so is the only
 * honest thing available.
 */
function SignaturePanel({
  doc,
}: {
  readonly doc: ListedDocument;
}): ReactNode {
  const { client } = usePortal();
  const [state, setState] = useState<EnvelopeState | null>(null);
  const [signerName, setSignerName] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [mode, setMode] = useState<SignatureMode>("on-site");
  const [verification, setVerification] = useState<SignatureVerification | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  if (!doc.issued) {
    return (
      <p className="small muted">
        A signature cannot be raised against a draft: the API binds an envelope
        to the document&rsquo;s hash, and asking somebody to sign bytes that can
        still change would make the signature worthless.
      </p>
    );
  }

  async function raise(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const envelope = await client.createSignatureEnvelope(doc.id, {
        mode,
        signerName,
        ...(signerEmail.trim() === "" ? {} : { signerEmail: signerEmail.trim() }),
      });
      setState({ envelope, reportedFor: "raised" });
      setVerification(null);
    } catch (cause: unknown) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  async function withdraw(envelopeId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const envelope = await client.voidSignatureEnvelope(envelopeId);
      setState({ envelope, reportedFor: "withdrawn" });
    } catch (cause: unknown) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  async function check(envelopeId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      setVerification(await client.verifySignature(envelopeId));
    } catch (cause: unknown) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  return (
    <details className="signature">
      <summary>Signature</summary>

      {state === null ? (
        <>
          <p className="small muted">
            No signature request has been raised from this browser. The API has
            no route that lists signature envelopes, so one raised anywhere else
            cannot be shown here.
          </p>
          <form className="stacked" onSubmit={(e) => void raise(e)}>
            <div className="field">
              <label htmlFor={`signer-${doc.id}`}>Who signs</label>
              <input
                id={`signer-${doc.id}`}
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor={`sig-mode-${doc.id}`}>How</label>
              <select
                id={`sig-mode-${doc.id}`}
                value={mode}
                onChange={(e) => setMode(e.target.value as SignatureMode)}
              >
                {SIGNATURE_MODES.map((option) => (
                  <option key={option} value={option}>
                    {SIGNATURE_MODE_LABEL[option]}
                  </option>
                ))}
              </select>
            </div>
            {mode === "remote" ? (
              <div className="field">
                <label htmlFor={`signer-email-${doc.id}`}>
                  Signer&rsquo;s email
                </label>
                <input
                  id={`signer-email-${doc.id}`}
                  value={signerEmail}
                  onChange={(e) => setSignerEmail(e.target.value)}
                />
                <p className="hint small">
                  The API requires it for a remote request: it is where the
                  signing link would go.
                </p>
              </div>
            ) : null}
            <button type="submit" disabled={busy || signerName.trim() === ""}>
              {busy ? "Working…" : "Raise a signature request"}
            </button>
            <p className="hint small">
              Raising a request sends nothing to the signer. The API exposes no
              route that marks an envelope as sent, so it stays outstanding
              until a signature is captured against it.
            </p>
          </form>
        </>
      ) : (
        <>
          <p>
            <span className={`status-word status-${envelopeTone(state.envelope.status)}`}>
              {envelopeStatusWord(state.envelope.status)}
            </span>{" "}
            <span className="muted small">
              {ENVELOPE_STATUS[state.envelope.status]?.meaning ?? ""}
            </span>
          </p>
          <p className="small muted">
            This is the status the API returned when the request was{" "}
            {state.reportedFor}. There is no route that reads an envelope back,
            so it cannot be refreshed — check the signature below to ask a
            question the API will answer.
          </p>

          <dl className="fields">
            <div className="row">
              <dt>Signer</dt>
              <dd>
                {state.envelope.signerName}
                {state.envelope.signerEmail === null ? null : (
                  <span className="muted small"> · {state.envelope.signerEmail}</span>
                )}
              </dd>
            </div>
            <div className="row">
              <dt>Mode</dt>
              <dd>{SIGNATURE_MODE_LABEL[state.envelope.mode] ?? state.envelope.mode}</dd>
            </div>
            <div className="row">
              <dt>Bound to</dt>
              <dd className="mono small">{state.envelope.documentHash}</dd>
            </div>
            <div className="row">
              <dt>Sent to the signer</dt>
              <dd>
                {state.envelope.sentAt === null ? (
                  <span className="muted">Never</span>
                ) : (
                  longDate(state.envelope.sentAt)
                )}
              </dd>
            </div>
            <div className="row">
              <dt>Signature captured</dt>
              <dd>
                {state.envelope.completedAt === null ? (
                  <span className="muted">Not yet</span>
                ) : (
                  longDate(state.envelope.completedAt)
                )}
              </dd>
            </div>
          </dl>

          <p className="pending">
            <button
              className="secondary"
              disabled={busy}
              onClick={() => void check(state.envelope.id)}
            >
              Check the signature
            </button>{" "}
            <button
              className="secondary"
              disabled={busy}
              onClick={() => void withdraw(state.envelope.id)}
            >
              Withdraw this request
            </button>
          </p>

          {verification === null ? null : (
            <p className="small">
              {verification.valid ? (
                <span className="status-word status-tracking">
                  Valid{" "}
                  <span className="muted">
                    · a signature is held and it still matches the document
                  </span>
                </span>
              ) : (
                <span className="status-word status-critical">
                  Not valid{" "}
                  <span className="muted">
                    · {verification.reason ?? "the API gave no reason"}
                  </span>
                </span>
              )}
            </p>
          )}
        </>
      )}

      {error !== null ? <Failure error={error} what="that signature request" /> : null}
    </details>
  );
}

function envelopeTone(status: SignatureEnvelope["status"]): string {
  if (status === "signed") return "tracking";
  if (status === "voided" || status === "declined") return "critical";
  return "approaching";
}
